"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { useStops } from "@/hooks/trip/useStops";
import { useTripPlanner } from "@/hooks/trip/useTripPlanner";
import { useAutoScroll } from "@/hooks/options/useAutoScroll";
import HeaderBar from "../components/panels/HeaderBar";
import ResultsPanel from "@/components/panels/ResultsPanel";
import { useMapState } from "@/hooks/map/useMap";
import { VehicleTripPanel } from "@/components/panels/VehicleTripPanel";
import { useCurrency } from "@/hooks/options/useCurrency";
import { useFuelCost } from "@/hooks/options/useFuelCost";
import {
  createClearStopResults,
  createClearTrip,
  createHandleMarkerDragEnd,
} from "@/lib/tripHandlers";
import { useAutoRecalculate } from "@/hooks/trip/useAutoRecalculate";
import { useVehicleLoading } from "@/hooks/vehicle/useVehicleLoading";
import { useResults } from "@/hooks/results/useResults";
import { useCategorisedRouteFlag } from "@/hooks/trip/useCategorisedRouteFlag";
import { useNoCategorySelect } from "@/hooks/options/useNoCategoryFuelValue";
import { useCursorWhileAny } from "@/lib/setCursor";

const DynamicMap = dynamic(() => import("../components/panels/MapPanel"), { ssr: false });

export default function Home() {
  const {
    vehicleLoading,
    setVehicleLoading,
    selectedVehicles,
    addVehicle,
    removeVehicle,
    clearVehicles
  } = useVehicleLoading();

  const {
    stops,
    setStops,
    shouldRecalculate,
    setShouldRecalculate,
    updateStop,
    addStop,
    removeStop,
    clearStop,
    reorderStops,
    clearTripInputs
  } = useStops();

  const {
    tripResult,
    tripLoading,
    tripVersion,
    planTrip,
    clearTripResults,
    setTripLoading,
    emissionsLoaded,
    setEmissionsLoaded,
    snapRadius,
    setSnapRadius
  } = useTripPlanner();

  const {
    hoveredStopIndex,
    setHoveredStopIndex,
    isDraggingMarker,
    setIsDraggingMarker,
    routePlotted,
    setRoutePlotted,
  } = useMapState();

  const {
    currency,
    updateCurrency,
  } = useCurrency();

  const { fuelCost, setValidFuelCost, onClearFuelCost } = useFuelCost();

  const clearStopResults = createClearStopResults(clearStop, clearTripResults, setEmissionsLoaded);
  const clearTrip = createClearTrip(clearTripInputs, clearTripResults);

  const handleMarkerDragEnd = useMemo(
    () =>
      createHandleMarkerDragEnd(
        setStops,
        setShouldRecalculate
      ),
    [
      setStops, 
      setShouldRecalculate, 
    ]
  );

  useAutoRecalculate({
    shouldRecalculate,
    setShouldRecalculate,
    stops,
    setTripLoading,
    planTrip,
  });

  const { autoScrollEnabled, setAutoScrollEnabled, resultsRef } = useAutoScroll({
    tripResult,
    routePlotted,
    emissionsLoaded,
    tripVersion,
  });

  const {useCategoriesFlag, setUseCategoriesFlag} = useCategorisedRouteFlag();

  const {
    selectedNoCategoryFuelValue, 
    setSelectedNoCategoryFuelValue
  } = useNoCategorySelect();

  const results = useResults(
    tripResult,
    selectedVehicles,
    parseInt(fuelCost, 10),
    selectedNoCategoryFuelValue
  )

  useCursorWhileAny([
    vehicleLoading,
    tripLoading,
    shouldRecalculate
  ]);

  return (
    <div className="trip-planner-container-dark">
      <HeaderBar
        autoScrollEnabled={autoScrollEnabled}
        setAutoScrollEnabled={setAutoScrollEnabled}
        useCategories={useCategoriesFlag}
        setUseCategories={setUseCategoriesFlag}
        currency={currency}
        updateCurrency={updateCurrency}
        selectedNoCategoryFuelValue={selectedNoCategoryFuelValue}
        setSelectedNoCategoryFuelValue={setSelectedNoCategoryFuelValue}
        snapRadius={snapRadius}
        setSnapRadius={setSnapRadius}
        setShouldRecalculate={setShouldRecalculate}
      />

      <div className="main-panel">
        <VehicleTripPanel
          stops={stops}
          hoveredStopIndex={hoveredStopIndex}
          isDraggingMarker={isDraggingMarker}
          tripLoading={tripLoading}
          addVehicle={addVehicle}
          removeVehicle={removeVehicle}
          clearVehicles={clearVehicles}
          selectedVehicles={selectedVehicles}
          vehicleLoading={vehicleLoading}
          setVehicleLoading={setVehicleLoading}
          updateStop={updateStop}
          addStop={addStop}
          removeStop={removeStop}
          clearStop={clearStopResults}
          reorderStops={reorderStops}
          currency={currency}
          fuelCost={fuelCost}
          setFuelCost={setValidFuelCost}
          onClearFuelCost={onClearFuelCost}
        />

        <div className="panel map-panel">
          <DynamicMap
            routeCoords={tripResult?.route || []}
            wayCategoryValues={tripResult?.wayCategory.values || null}
            wayCategorySummary={tripResult?.wayCategory.summary || null}
            useCategories={useCategoriesFlag}
            stops={stops}
            setHoveredStopIndex={setHoveredStopIndex}
            isDraggingMarker={isDraggingMarker}
            setIsDraggingMarker={setIsDraggingMarker}
            onClearTrip={clearTrip}
            onMarkerDragEnd={handleMarkerDragEnd}
            tripLoading={tripLoading}
            onRoutePlotComplete={() => setRoutePlotted(true)}
          />
        </div>
      </div>

      <ResultsPanel
        tripResult={tripResult}
        results={results}
        loading={tripLoading}
        setEmissionsLoaded={setEmissionsLoaded}
        resultsRef={resultsRef}
        costEntered={fuelCost!=""}
        currency={currency}
      />
            
    </div>
  );
}
